import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, GraduationCap, Briefcase, Globe2, Linkedin, BookOpen, Award, Languages } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, API } from "@/lib/api";
import { Reveal } from "@/components/ui-bits";

export default function ExpertProfile() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const [expert, setExpert] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/experts/${id}`).then((r) => setExpert(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return <div className="mx-auto max-w-7xl px-4 py-32 text-center text-slate-500" data-testid="expert-not-found">{t("notFound.title")}</div>;
  if (!expert)
    return <div className="mx-auto max-w-7xl px-4 py-32 text-center text-slate-400">{t("common.loading")}</div>;

  const photo = expert.photo_url || (expert.photo_path ? `${API}/expert-photo/${expert.id}` : null);
  const links = [
    [Linkedin, "LinkedIn", expert.linkedin],
    [BookOpen, "Google Scholar", expert.google_scholar],
    [Award, "ORCID", expert.orcid],
  ].filter((l) => l[2]);

  return (
    <div data-testid="expert-profile-page">
      <section className="bg-navy-950 pb-40 pt-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Link to="/experts" data-testid="expert-back-link" className="text-xs font-bold uppercase tracking-wider text-gold hover:text-gold-light">
            ← {t("page.expertsTitle")}
          </Link>
        </div>
      </section>
      <section className="mx-auto -mt-32 max-w-7xl px-4 pb-20 sm:px-6 lg:px-12">
        <Reveal className="overflow-hidden rounded-lg bg-white shadow-2xl">
          <div className="grid lg:grid-cols-[340px_1fr]">
            <div className="relative aspect-[4/5] bg-slate-100 lg:max-h-[560px] lg:self-start">
              {photo ? (
                <img src={photo} alt={expert.full_name} className="absolute inset-0 h-full w-full object-cover object-top" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-navy font-display text-6xl text-gold">
                  {expert.full_name?.charAt(0)}
                </div>
              )}
            </div>
            <div className="p-8 lg:p-12">
              <h1 className="font-display text-4xl font-semibold text-navy" data-testid="expert-name">{expert.full_name}</h1>
              <p className="mt-2 text-lg text-slate-600">{expert.job_title}</p>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
                {expert.country && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-gold" />{expert.city ? `${expert.city}, ` : ""}{expert.country}</span>}
                {expert.highest_degree && <span className="inline-flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-gold" />{expert.highest_degree}{expert.university ? ` — ${expert.university}` : ""}</span>}
                {expert.years_experience > 0 && <span className="inline-flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-gold" />{expert.years_experience}+ {t("experts.years")}</span>}
                {expert.languages?.length > 0 && <span className="inline-flex items-center gap-1.5"><Languages className="h-4 w-4 text-gold" />{expert.languages.join(", ")}</span>}
              </div>
              {expert.expertise_areas?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {expert.expertise_areas.map((a) => (
                    <span key={a} className="rounded-full bg-gold-soft px-3 py-1.5 text-xs font-bold text-gold-hover">{a}</span>
                  ))}
                </div>
              )}
              {expert.bio && <p className="mt-6 text-base leading-relaxed text-slate-700">{expert.bio}</p>}
              <div className="mt-8 grid gap-5 border-t border-slate-100 pt-6 sm:grid-cols-2">
                {expert.training_experience && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gold-hover">{t("join.trainingExp")}</h4>
                    <p className="mt-1.5 text-sm text-slate-600">{expert.training_experience}</p>
                  </div>
                )}
                {expert.consulting_experience && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gold-hover">{t("join.consultingExp")}</h4>
                    <p className="mt-1.5 text-sm text-slate-600">{expert.consulting_experience}</p>
                  </div>
                )}
                {expert.sector_experience && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gold-hover">{t("join.sectorExp")}</h4>
                    <p className="mt-1.5 text-sm text-slate-600">{expert.sector_experience}</p>
                  </div>
                )}
                {expert.work_regions?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gold-hover"><Globe2 className="me-1 inline h-3.5 w-3.5" />{t("join.workRegions")}</h4>
                    <p className="mt-1.5 text-sm text-slate-600">{expert.work_regions.join(", ")}</p>
                  </div>
                )}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to={`/experts/${expert.id}/book`} data-testid="expert-book-cta" className="rounded-full bg-gold px-7 py-3 text-sm font-bold text-navy transition-colors hover:bg-gold-hover">
                  {t("nav.book")}
                </Link>
                {links.map(([Icon, label, url]) => (
                  <a key={label} href={url} target="_blank" rel="noopener noreferrer" data-testid={`expert-link-${label.toLowerCase().replace(" ", "-")}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-3 text-xs font-semibold text-slate-600 transition-colors hover:border-gold hover:text-gold-hover">
                    <Icon className="h-4 w-4" /> {label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
